import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  signal,
  OnInit,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import {
  BaseCreationDialog,
  BaseDialogConfig,
} from '@shared/components/base-creation-dialog/base-creation-dialog';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { StepLead } from '@shared/components/step-lead/step-lead';
import { SummarySection, SummaryRow } from '@shared/components/summary-section/summary-section';
import { EquipmentStore } from '@features/catalog/store/equipment.store';
import {
  CreateInverterDto,
  SolarInverter,
} from '@features/catalog/models/inverter.model';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

@Component({
  selector: 'app-create-inverter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ...BrnDialogImports,
    ...HlmDialogImports,
    HlmSeparatorDirective,
    WizardDialogHeader,
    WizardDialogFooter,
    SuffixInput,
    StepLead,
    SummarySection,
    SummaryRow,
  ],
  templateUrl: './create-inverter-dialog.html',
  styleUrl: './create-inverter-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateInverterDialog extends BaseCreationDialog implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly equipmentStore = inject(EquipmentStore);
  private readonly toastService = inject(ToastService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);

  // Input for editing
  editInverter = input<SolarInverter | null>(null);

  // Override config input with specific dialog configuration
  override config = input<BaseDialogConfig>({
    title: 'Neuen Wechselrichter erstellen',
    description:
      'Erstellen Sie einen neuen Wechselrichter mit Leistung in kW und Preis in EUR',
    stepLabels: ['Wechselrichter Details', 'Kosten'],
    stepLeads: [
      'Technische Daten des Wechselrichters.',
      'Prüfen Sie die Angaben vor dem Speichern.',
    ],
    submitButtonText: 'Wechselrichter erstellen',
    eyebrow: 'Katalog · Wechselrichter',
  });

  // Computed config based on edit mode for display purposes
  computedTitle = computed(() => {
    const editing = !!this.editInverter();
    return editing
      ? 'Wechselrichter bearbeiten'
      : 'Neuen Wechselrichter erstellen';
  });

  computedDescription = computed(() => {
    const editing = !!this.editInverter();
    return editing
      ? 'Bearbeiten Sie Leistung (kW) und Preis (EUR) des Wechselrichters'
      : 'Erstellen Sie einen neuen Wechselrichter mit Leistung (kW) und Preis (EUR)';
  });

  computedSubmitButtonText = computed(() => {
    const editing = !!this.editInverter();
    return editing ? 'Wechselrichter speichern' : 'Wechselrichter erstellen';
  });

  // Outputs - delegate to the base class's submitted output
  inverterCreated = output<SolarInverter>();
  inverterUpdated = output<SolarInverter>();

  // Form
  inverterForm: FormGroup;

  constructor() {
    super();
    this.inverterForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      power: ['', [Validators.required, Validators.min(0.1)]],
      price: ['', [Validators.required, Validators.min(0)]],
    });

    // Use effect to populate form when editInverter changes
    effect(() => {
      const inverter = this.editInverter();
      if (inverter) {
        this.populateForm(inverter);
      }
    });
  }

  ngOnInit() {
    // Initialization if needed
  }

  private populateForm(inverter: SolarInverter | null): void {
    if (inverter) {
      this.inverterForm.patchValue({
        name: inverter.name,
        power: inverter.size,
        price: inverter.cost,
      });
    }
  }

  override canProceedFromCurrentStep(): boolean {
    const step = this.currentStep();
    switch (step) {
      case 1:
        // Step 1: Check inverter details (name, power)
        return (
          this.inverterForm.get('name')?.valid === true &&
          this.inverterForm.get('power')?.valid === true
        );
      case 2:
        // Step 2: All fields valid
        return this.inverterForm.valid;
      default:
        return false;
    }
  }

  // Reset dialog state
  protected override resetDialog(): void {
    super.resetDialog();
    this.inverterForm.reset();
  }

  // Handle submission
  protected override async handleSubmit(dialogContext: any): Promise<void> {
    if (!this.inverterForm.valid) {
      throw new Error('Bitte füllen Sie alle erforderlichen Felder aus');
    }

    const formData = this.inverterForm.value;
    const payload: CreateInverterDto = {
      name: formData.name,
      size: formData.power,
      cost: formData.price,
    };

    const orgId = this.currentOrgStore.organizationId();
    if (!orgId) {
      throw new Error('Organisations-ID nicht verfügbar');
    }

    const editingInverter = this.editInverter();
    let result: SolarInverter;

    if (editingInverter) {
      // Update mode
      result = await this.equipmentStore.updateInverter(
        orgId,
        editingInverter.id,
        payload,
      );
      this.toastService.success('Wechselrichter erfolgreich aktualisiert');
      this.submitted.emit(result);
      this.inverterUpdated.emit(result);
    } else {
      // Create mode
      result = await this.equipmentStore.createInverter(orgId, payload);
      if (!result) {
        throw new Error('Fehler beim Erstellen des Wechselrichters');
      }
      this.toastService.success('Wechselrichter erfolgreich erstellt');
      this.submitted.emit(result);
      this.inverterCreated.emit(result);
    }
  }
}
